import React from 'react';
import { Modal, Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getUrlParam, isKlarityHost } from 'utils/common';
import { PATH } from 'constants/path';
import { MODE } from 'constants/common';
import { authUserWithSwitchKey, opsExchangeCode, ERegisterType } from 'api/public';
import { processAuthResponse } from 'utils/auth';
import { setPreviewScheduleToken, setPreviewProviderId, setViewMode } from 'utils/sessionStorage';
import ProviderStore from 'store/Provider';
import { getCurrentUser } from 'api/common';
import { checkLeadFormByEmail } from 'api/ppaLeadForm';
import { getIntentRedirect } from 'pages/PPALeadFormPage/getIntentRedirect';

const SwitchAccountRedirectPage = () => {
    const navigate = useNavigate();
    const code = getUrlParam('code');
    const accountType = getUrlParam('accountType');
    const [, set] = ProviderStore.useStore();

    const [showModal] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);

    const navigateByIntent = async () => {
        try {
            const userRes = await getCurrentUser();
            const email = userRes?.data?.data?.actingUser?.email;
            if (email) {
                const leadRes = await checkLeadFormByEmail({ email });
                const practiceHelpType = leadRes?.data?.data?.leadForm?.practiceHelpType;
                if (practiceHelpType) {
                    const target = await getIntentRedirect(practiceHelpType);
                    navigate(target);
                    return;
                }
            }
        } catch (error) {
            console.error(error);
        }
        navigate(PATH.DASHBOARD);
    };

    const getHashCode = (): string | null => {
        const { hash } = window.location;
        if (!hash) return null;
        const params = new URLSearchParams(hash.slice(1));
        return params.get('code');
    };

    const handleOpsExchange = async (opsCode: string) => {
        const result = await opsExchangeCode(opsCode);
        if (result.error || result.data?.code !== 200) {
            message.error(result.data?.message || 'Failed to exchange code');
            setTimeout(() => navigate(PATH.DASHBOARD), 2000);
            return;
        }
        const { data } = result.data;
        setPreviewScheduleToken(data.token);
        setPreviewProviderId(String(data.provider?.id || ''));
        setViewMode(MODE.OPS_PREVIEW);
        window.history.replaceState({}, '', window.location.pathname);
        await navigateByIntent();
    };

    const handleSwitchAccountCode = async () => {
        try {
            if (isProcessing) return;
            setIsProcessing(true);

            const hashCode = getHashCode();
            if (hashCode) {
                await handleOpsExchange(hashCode);
                return;
            }

            if (code) {
                const authResult = await authUserWithSwitchKey(code);

                if (authResult.error) {
                    message.error('Failed to authenticate with switch key');
                    setTimeout(() => {
                        navigate(PATH.DASHBOARD);
                    }, 2000);
                    return;
                }
                const platformType = isKlarityHost() ? ERegisterType.KLARITY_PORTAL : ERegisterType.KIWI_PORTAL;
                const loginResult = processAuthResponse(
                    authResult,
                    set,
                    // @ts-ignore
                    accountType || platformType,
                );

                if (loginResult) {
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.delete('code');
                    newUrl.searchParams.delete('accountType');
                    window.history.replaceState({}, '', newUrl.toString());

                    message.success('Account switched successfully!');
                    await navigateByIntent();
                } else {
                    message.error('Failed to process authentication response');
                    setTimeout(() => {
                        navigate(PATH.DASHBOARD);
                    }, 2000);
                }
            } else {
                navigate(PATH.DASHBOARD);
            }
        } catch (switchError) {
            message.error('An error occurred while switching account. Redirecting to dashboard...');
            setTimeout(() => {
                navigate(PATH.DASHBOARD);
            }, 2000);
        } finally {
            setIsProcessing(false);
        }
    };

    React.useEffect(() => {
        handleSwitchAccountCode();
    }, []);

    return (
        <div style={{ minHeight: '300px', textAlign: 'center', lineHeight: '300px' }}>
            <Spin spinning={isProcessing} size="large">
                <div style={{ padding: '20px' }}>
                    <p>Please wait while we process your account switch.</p>
                </div>
            </Spin>
            <Modal open={showModal} title="Account Switch" footer={null}>
                <div>Processing account switch...</div>
            </Modal>
        </div>
    );
};

export default SwitchAccountRedirectPage;
